/**
 * Barre d'action fixe mobile du configurateur GO!.
 *
 * Trois responsabilites, toutes en lecture du DOM rendu par le serveur :
 *  - afficher la barre quand le bouton d'ajout principal quitte le viewport ;
 *  - refleter la configuration en cours (valeurs cochees du configurateur) ;
 *  - refleter le prix, qui est reecrit par product-info.js via la Section
 *    Rendering API a chaque changement de variante.
 *
 * Aucune logique panier ici : le bouton porte `form`, donc <product-form>
 * intercepte la soumission comme pour le bouton principal.
 */
class GoStickyAtc extends HTMLElement {
  connectedCallback() {
    this.anchor = document.getElementById(this.dataset.anchor);
    this.configEl = this.querySelector('[data-config]');
    this.priceEl = this.querySelector('[data-price]');
    this.priceSource = document.getElementById(this.dataset.priceSource);
    this.variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);

    this.onChange = this.onChange.bind(this);
    this.onInteract = this.onInteract.bind(this);

    this.packshot = document.getElementById(this.dataset.packshot);
    this.thumbEl = this.querySelector('[data-thumb]');

    this.observeAnchor();
    this.observePackshot();
    this.observePrice();
    this.observeThumb();

    // Le configurateur est remplace par la Section Rendering API a chaque
    // changement : on ecoute au niveau du document pour survivre au remplacement.
    document.addEventListener('change', this.onChange);

    // Meme raison pour l'ecoute de l'interaction : les pastilles sont remplacees
    // a chaque choix, un ecouteur pose sur elles ne survivrait pas au premier.
    document.addEventListener('click', this.onInteract);
    document.addEventListener('change', this.onInteract);

    // L'ajout au panier retire la barre : voir `onCartUpdate`.
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));

    this.syncConfig();
    this.syncPrice();
  }

  /**
   * L'ajout au panier remet le composant a l'etat « rien n'a ete demande ».
   *
   * La barre repond a une question — « vous avez compose ceci, voulez-vous
   * l'ajouter ? ». Une fois l'ajout fait, la question n'a plus lieu d'etre : sur
   * mobile la popup de confirmation monte du bas et la barre restait dessous, a
   * proposer une seconde fois ce qui vient d'entrer dans le panier, en mangeant
   * un bandeau de hauteur d'ecran jusqu'a la fin de la visite.
   *
   * On remet `interacted` a false plutot que de masquer la barre une fois pour
   * toutes : la selection de variante, elle, n'est PAS reinitialisee — remettre
   * le configurateur a zero effacerait un choix que le visiteur vient de faire
   * et voudra peut-etre decliner en une seconde couleur. Toucher a nouveau une
   * pastille suffit donc a rappeler la barre, avec la nouvelle configuration.
   */
  onCartUpdate(event) {
    if (event?.source !== 'product-form') return;

    this.interacted = false;
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onChange);
    document.removeEventListener('click', this.onInteract);
    document.removeEventListener('change', this.onInteract);
    this.cartUpdateUnsubscriber?.();
    this.anchorObserver?.disconnect();
    this.packshotObserver?.disconnect();
    this.priceObserver?.disconnect();
    this.thumbObserver?.disconnect();
    document.body.classList.remove('go-sticky-open');
  }

  get sectionId() {
    return (this.dataset.anchor || '').replace('ProductSubmitButton-', '');
  }

  /**
   * La barre s'affiche entre deux reperes : une fois le pack-shot SORTI de
   * l'ecran apres y etre entre, et tant que le bouton principal n'est pas revenu.
   *
   * Le bouton seul ne suffit pas. Sur l'accueil le configurateur est au milieu
   * de la page : le bouton est hors champ des le haut du document, et la barre
   * se serait affichee avant meme qu'on ait vu le produit.
   *
   * ⚠ « Sorti de l'ecran » n'est pas « pas a l'ecran ».
   *
   * La condition etait `packshotSeen !== true`, vraie aussi bien apres la sortie
   * du pack-shot qu'AVANT sa premiere apparition — donc vraie des le chargement
   * de l'accueil, ou le configurateur est sous la ligne de flottaison : la barre
   * s'affichait immediatement, ce que les deux reperes devaient justement
   * empecher. Il faut donc retenir que le pack-shot a ete vu au moins une fois.
   *
   * Sans pack-shot declare — ou sans IntersectionObserver — `packshotEverSeen`
   * reste `undefined` et l'on retombe sur le seul bouton.
   *
   * TROISIEME condition : le visiteur doit avoir touche au configurateur.
   *
   * Une variante est selectionnee des le chargement, donc la barre avait tout ce
   * qu'il faut a montrer sans que personne n'ait rien demande : un simple
   * defilement la declenchait. Or elle rappelle « votre configuration » — il faut
   * qu'il y en ait une, choisie. Tant que rien n'est touche, elle ne parait
   * jamais, meme apres avoir parcouru toute la page.
   */
  get visible() {
    if (!this.interacted) return false;
    if (this.anchorSeen !== false) return false;
    if (!this.packshot || typeof IntersectionObserver === 'undefined') return true;
    return this.packshotEverSeen === true && this.packshotSeen === false;
  }

  /**
   * Retient la premiere interaction reelle avec le configurateur.
   *
   * ⚠ `event.isTrusted` est le cœur du dispositif, pas une precaution.
   *
   * Le theme declenche lui-meme des clics au chargement : `GoConfigSteps.openDefault`
   * coche le premier coloris de la famille la moins chere, `go-variant-url`
   * restaure la variante de l'URL, `go-visual-swipe` clique un radio a chaque
   * geste. Un `click()` appele en JS produit un evenement NON approuve, un doigt
   * ou une souris un evenement approuve : c'est la seule facon de distinguer
   * « le visiteur a choisi » de « le theme s'est initialise », et sans ce filtre
   * la barre reapparaitrait des le chargement, exactement comme avant.
   *
   * Le geste sur la bande de pack-shots compte aussi : il finit par un clic
   * programmatique sur le radio, mais il commence par un vrai clic ou un vrai
   * pointeur sur le visuel, capte ici par `go-visual-swipe`.
   */
  onInteract(event) {
    if (this.interacted || !event.isTrusted) return;

    const target = event.target;
    if (!target?.closest) return;

    const inPicker = target.closest(`variant-selects[data-section="${this.sectionId}"]`);
    const inVisual = target.closest('go-visual-swipe');
    if (!inPicker && !inVisual) return;

    this.interacted = true;
    this.render();
  }

  render() {
    this.classList.toggle('go-sticky--visible', this.visible);
    // La barre masque le bas du document : on lui reserve sa place, et
    // seulement quand elle est la.
    document.body.classList.toggle('go-sticky-open', this.visible);
  }

  observeAnchor() {
    if (!this.anchor || typeof IntersectionObserver === 'undefined') return;

    this.anchorObserver = new IntersectionObserver(
      ([entry]) => {
        this.anchorSeen = entry.isIntersecting;
        this.render();
      },
      { rootMargin: '0px 0px -20% 0px' }
    );
    this.anchorObserver.observe(this.anchor);
  }

  observePackshot() {
    if (!this.packshot || typeof IntersectionObserver === 'undefined') return;

    this.packshotObserver = new IntersectionObserver(([entry]) => {
      this.packshotSeen = entry.isIntersecting;
      // Une fois vrai, toujours vrai : c'est ce qui distingue « le pack-shot est
      // sorti » de « on ne l'a pas encore atteint ».
      if (entry.isIntersecting) this.packshotEverSeen = true;
      this.render();
    });
    this.packshotObserver.observe(this.packshot);
  }

  /**
   * La vignette recopie le visuel principal, quel qu'il soit : la bande de
   * pack-shots sur l'accueil, la galerie sur la fiche produit. Aucune table de
   * variantes a tenir a jour — ce qui est a l'ecran est, par construction, la
   * bonne image.
   */
  observeThumb() {
    if (!this.packshot || !this.thumbEl || typeof MutationObserver === 'undefined') return;

    this.thumbObserver = new MutationObserver(() => this.syncThumb());
    this.thumbObserver.observe(this.packshot, {
      childList: true,
      subtree: true,
      attributes: true,
      // `data-swipe-active` autant que `src` : un coup de chevron deplace la
      // marque de vignette active sans changer aucune source.
      attributeFilter: ['src', 'data-swipe-active'],
    });
    this.syncThumb();
  }

  syncThumb() {
    // La vignette active d'abord, le premier `img` ensuite. Sur l'accueil le
    // visuel est la bande de `<go-visual-swipe>`, montee en [precedent, actif,
    // suivant] : le premier `img` du DOM y est le coloris VOISIN. La galerie de
    // la fiche produit, elle, remonte le media actif en tete — d'ou le repli.
    const source = this.packshot?.querySelector('[data-swipe-active] img') || this.packshot?.querySelector('img');
    const url = source?.currentSrc || source?.src;
    if (!url || url === this.thumbUrl) return;

    this.thumbUrl = url;
    const image = new Image();
    image.alt = '';
    image.src = url;
    this.thumbEl.replaceChildren(image);
  }

  observePrice() {
    if (typeof MutationObserver === 'undefined') return;

    // On observe un ancetre stable plutot que le bloc prix lui-meme :
    // product-info.js peut remplacer ce noeud, ce qui tuerait un observer
    // attache directement dessus.
    const root = this.closest('product-info') || document.getElementById(`MainProduct-${this.sectionId}`);
    if (!root) return;

    this.priceObserver = new MutationObserver(() => this.syncPrice());
    this.priceObserver.observe(root, { childList: true, subtree: true, characterData: true });
  }

  onChange(event) {
    if (!event.target.closest?.('variant-selects')) return;
    this.syncConfig();
  }

  /**
   * Rappel de configuration, en version compacte.
   *
   * Seul le COLORIS, et sans son intitule d'option : « Velours · Vert d'eau ».
   * La barre dispose d'environ 180px sur un ecran de 390px, la vignette et le
   * bouton places. « Tissu : Velours — Vert d'eau · Pietement : Chene massif »
   * n'y entrait pas et se faisait couper au milieu du coloris — donc exactement
   * sur l'information qui compte.
   *
   * Le pietement est retire volontairement : il ne rentrera jamais, et c'est le
   * moins visuel des trois choix. La vignette montre la matiere, le rappel la
   * nomme ; le pietement se relit dans le configurateur, a une remontee de la —
   * que la zone de rappel declenche justement d'un clic.
   *
   * Le separateur de famille du catalogue devient un point median : meme role,
   * moitie moins large.
   */
  syncConfig() {
    if (!this.configEl) return;

    const source = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`) || this.variantSelects;
    if (!source) return;

    // Les pastilles rondes SONT le coloris : c'est ainsi que `go-visual-swipe`
    // reconnait deja cette option en JS, sans supposer ni sa position ni son nom.
    // Sans elles — option non decoupee en familles — on retombe sur la premiere
    // valeur cochee plutot que de n'afficher rien.
    const shade =
      source.querySelector('.go-group--pastille input[type="radio"]:checked') ||
      source.querySelector('fieldset input:checked');
    if (!shade) return;

    const text = GoStickyAtc.compact(shade.value);
    if (text === this.configEl.textContent) return;

    this.configEl.textContent = text;
  }

  /**
   * Le separateur de famille ramene au point median.
   *
   * La liste est celle de `go-fabric-families.liquid`, seule source de la regle
   * de nommage : le tiret cadratin, le demi-cadratin, la barre oblique, le
   * deux-points, et le tiret entoure d'espaces. Seule la PREMIERE occurrence est
   * remplacee — « Velours — Bleu — nuit » garderait son second tiret, comme
   * la derivation de famille ne coupe qu'au premier.
   */
  static compact(value) {
    return String(value || '')
      .replace(/\s*(?:—|–|\/|:|\s-\s)\s*/, ' · ')
      .trim();
  }

  syncPrice() {
    if (!this.priceEl) return;

    const source = document.getElementById(this.dataset.priceSource);
    if (!source) return;

    // Le bloc prix contient une version barree et une version courante ;
    // on ne garde que le premier montant visible.
    const current = source.querySelector('.price-item--sale, .price-item--regular') || source;
    const text = current.textContent.trim().replace(/\s+/g, ' ');

    // Garde obligatoire : l'observateur surveille un ancetre qui contient aussi
    // cette barre, donc toute ecriture ici le re-declenche. Sans la comparaison,
    // observer -> ecriture -> observer boucle et gele le thread principal.
    if (!text || text === this.priceEl.textContent) return;

    this.priceEl.textContent = text;
  }
}

// Meme garde que les autres composants GO! : deux balises <script src>
// identiques sur une page s'executent deux fois, et le second passage
// redefinirait un nom deja pris.
if (!customElements.get('go-sticky-atc')) {
  customElements.define('go-sticky-atc', GoStickyAtc);
}
